// src/components/RechargeHub.jsx
//
// "Reset & Recharge" — the recovery half of the product. Tone from
// calculateScores(currentUser) but deliberately NO numbers on the page.
//
// 1. Energy check-in -> a suggested reset (logged, so it becomes a trend)
// 2. Tools we own (data/resetTools.js): breath pacers and timed guides with
//    on-screen cues. Finishing one logs a session to the private journal and
//    ticks the Guided Path recovery checkpoint (Running Hot -> here).
// 3. "Sessions from around the web": curated third-party YouTube videos,
//    clearly labelled as not ours, demoted below the tools.
//
// Session memory lives in users/{uid}/private_journal/recharge_log (self-only)
// — it used to sit on the admin/coach-readable user doc.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Wind, Moon, Waves, Sparkles, HeartHandshake, X, Play, History, Youtube, Timer } from 'lucide-react';
import { calculateScores } from '../utils/scoring';
import { useOrganization } from '../context/OrganizationContext';
import { copyFor, isCorporateOrg } from '../data/copyDictionary';
import { RESET_TOOLS, getResetTool, suggestForEnergy, suggestionCopy } from '../data/resetTools';
import { subscribeJournalEntry, saveJournalEntry } from '../utils/journalStore';
import { PREVIEW_UID, markPathNodeDone, pathNodeIdsForTopic } from '../utils/guidedPath';
import SupportOutlet from './dashboard/SupportOutlet';
import CustomYouTubePlayer from '../shared/CustomYouTubePlayer';
import ResetTool from './recharge/ResetTool';
import WindDownCard from './recharge/WindDownCard';
import DecompressCard from './recharge/DecompressCard';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { getYouTubeVideoId } from '../utils/youtube';

// --- Third-party sessions (YouTube). Not produced by EAS — shown as a shelf. ---
const WEB_SESSIONS = [
    { id: 'breathe', label: 'Breathe', icon: Wind, tagClass: 'bg-sky-50 text-sky-700 border-sky-100', sessions: [
        { id: 'breathwork-reset', title: 'Breathwork Reset', desc: 'Slow, guided breathing to settle your nervous system.', videoId: 'a4FvInzzcs4', source: 'Feeling Healing' }
    ] },
    { id: 'rest', label: 'Rest', icon: Moon, tagClass: 'bg-indigo-50 text-indigo-700 border-indigo-100', sessions: [
        { id: 'nsdr', title: 'Deep Rest (NSDR) Protocol', desc: 'Non-sleep deep rest to restore energy without a nap.', videoId: 'pL02HRFk2vo', source: 'YouTube' },
        { id: 'yoga-nidra', title: 'Yoga Nidra', desc: 'A guided journey into deep, restful stillness.', videoId: 'zPz-V93zHPk', source: 'Caren Hope' },
        { id: 'post-game', title: 'Post-Game Decompress', corporateTitle: 'After-Work Decompress', desc: 'Let the intensity drain away after a big effort.', videoId: '2IJUD-e14FY', source: 'Hospital for Special Surgery' }
    ] },
    { id: 'move', label: 'Move', icon: Waves, tagClass: 'bg-teal-50 text-teal-700 border-teal-100', sessions: [
        { id: 'hips-back', title: 'Yoga for Hips & Back', desc: 'Gentle stretches to release where tension likes to hide.', videoId: 'g_tea8ZNk5A', source: 'YouTube' },
        { id: 'morning-flow', title: 'Morning Flow', desc: 'An easy flow to wake the body up softly.', videoId: 'g6DdOHaAmSQ', source: 'Yoga With Adriene' }
    ] },
    { id: 'meditate', label: 'Meditate', icon: Sparkles, tagClass: 'bg-violet-50 text-violet-700 border-violet-100', sessions: [
        { id: 'mindful-meditation', title: 'Mindful Meditation', desc: 'Come back to the present moment, one breath at a time.', videoId: 'ZToicYcHIOU', source: 'YouTube' }
    ] }
];

// Soft relative time — a reflective note, not a streak counter.
const relativeTime = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const days = Math.floor((Date.now() - then) / 86400000);
    if (days <= 0) return 'earlier today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks === 1) return 'last week';
    if (weeks < 5) return `${weeks} weeks ago`;
    return 'a while ago';
};

const LOG_ID = 'recharge_log';

const SupportCard = ({ elevated, onNavigate, currentUser }) => (
    <div className={`rounded-3xl border shadow-sm p-6 ${elevated ? 'bg-rose-50 border-rose-100' : 'bg-white border-slate-200'}`}>
        <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${elevated ? 'bg-rose-100 text-rose-500' : 'bg-brand-light text-brand-primary'}`}>
                <HeartHandshake size={22} />
            </div>
            <div className="flex-1">
                <h3 className="font-bold text-slate-800">It can help to talk to someone</h3>
                <p className="text-sm text-slate-600 mt-1">Recovery isn't something you have to do alone. If things feel heavy right now, reaching out is a strong next step — not a setback.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                    <SupportOutlet currentUser={currentUser} label="Talk to someone" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${elevated ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-brand-primary hover:bg-brand-dark text-white'}`} />
                    <button onClick={() => onNavigate && onNavigate('mental-health')} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${elevated ? 'bg-white text-rose-600 border border-rose-200 hover:bg-rose-100/60' : 'bg-brand-light text-brand-dark hover:bg-slate-100 border border-slate-200'}`}>Explore support options</button>
                </div>
            </div>
        </div>
    </div>
);

const RechargeHub = ({ currentUser, onNavigate }) => {
    const { orgData, isPreviewMode } = useOrganization();
    const isCorporate = isCorporateOrg(orgData);
    const scores = useMemo(() => calculateScores(currentUser), [currentUser]);
    const uid = isPreviewMode ? PREVIEW_UID : currentUser?.uid;

    const [log, setLog] = useState(null);           // { entries: [...] }
    const [energy, setEnergy] = useState(null);      // today's check-in (1-10)
    const [activeTool, setActiveTool] = useState(null);
    const [activeSession, setActiveSession] = useState(null); // web video
    const sessionTitle = (session) => (isCorporate && session?.corporateTitle) ? session.corporateTitle : session?.title;

    useEffect(() => {
        if (!uid) return undefined;
        const unsub = subscribeJournalEntry(uid, LOG_ID, (e) => setLog(e));
        return () => unsub();
    }, [uid]);

    // CMS-managed sessions (Resources tab -> "Reset & Recharge sessions"): global
    // library + this org's private library, unioned with the built-in shelf.
    const [cmsSessions, setCmsSessions] = useState([]);
    useEffect(() => {
        const unsubs = [];
        const globalRef = collection(db, 'recharge_sessions');
        let globalItems = [], privateItems = [];
        const publish = () => setCmsSessions([...globalItems, ...privateItems]);
        unsubs.push(onSnapshot(globalRef, (snap) => { globalItems = snap.docs.map(d => ({ id: d.id, ...d.data(), _scope: 'global' })); publish(); }, () => { /* rules not deployed yet -> base shelf only */ }));
        if (orgData?.id) {
            unsubs.push(onSnapshot(doc(db, 'organizations', orgData.id, 'private_libraries', 'recharge_sessions'), (s) => { privateItems = (s.exists() ? (s.data().resources || []) : []).map(r => ({ ...r, _scope: 'private' })); publish(); }, () => {}));
        }
        return () => unsubs.forEach(u => u());
    }, [orgData?.id]);

    const shelf = useMemo(() => {
        const industry = orgData?.industry === 'corporate' ? 'corporate' : 'sport';
        const cats = WEB_SESSIONS.map(c => ({ ...c, sessions: [...c.sessions] }));
        const more = { id: 'more', label: 'More', icon: Sparkles, tagClass: 'bg-slate-50 text-slate-600 border-slate-200', sessions: [] };
        cmsSessions.forEach(item => {
            if (Array.isArray(item.verticals) && item.verticals.length && !item.verticals.includes(industry)) return;
            const videoId = getYouTubeVideoId(item.url);
            if (!videoId) return;
            const tags = (Array.isArray(item.tags) ? item.tags : []).map(t => String(t).toLowerCase());
            const cat = cats.find(c => tags.includes(c.id) || tags.includes(c.label.toLowerCase())) || more;
            if (cat.sessions.some(s => s.videoId === videoId)) return;
            cat.sessions.push({ id: `cms_${item.id || videoId}`, title: item.title, desc: item.content || '', videoId, source: item.guest || (item._scope === 'private' ? 'Your organization' : 'YouTube') });
        });
        if (more.sessions.length) cats.push(more);
        return cats;
    }, [cmsSessions, orgData?.industry]);

    const entries = useMemo(() => (Array.isArray(log?.entries) ? log.entries : []), [log]);
    const last = entries.length ? entries[entries.length - 1] : null;
    const lastLabel = relativeTime(last?.at);
    const weekCount = useMemo(() => entries.filter(e => Date.now() - new Date(e.at).getTime() < 7 * 86400000).length, [entries]);

    const appendLog = useCallback(async (entry) => {
        if (!uid) return;
        const next = [...entries, { at: new Date().toISOString(), ...entry }].slice(-200);
        try {
            await saveJournalEntry(uid, LOG_ID, {
                worksheetId: LOG_ID, kind: 'recharge', title: 'Reset & Recharge log', topicId: 'reset-recharge',
                entries: next,
                progress: { answered: next.length, total: next.length, pct: 100 }
            });
        } catch (e) { console.error('recharge log failed', e); }
    }, [uid, entries]);

    const finishTool = async (result) => {
        const isWindDown = result.toolId === 'winddown';
        await appendLog({ type: isWindDown ? 'winddown' : 'tool', ...result });
        // Running Hot -> recovery checkpoint on the mountain; wind-down also counts for sleep
        const ids = new Set([...pathNodeIdsForTopic('reset-recharge'), ...(isWindDown ? pathNodeIdsForTopic('sleep') : [])]);
        for (const id of ids) {
            try { await markPathNodeDone(uid, id); } catch (e) { /* non-fatal */ }
        }
    };
    const lastWindDown = useMemo(() => { const e = [...entries].reverse().find(x => x.type === 'winddown'); return e ? relativeTime(e.at) : null; }, [entries]);

    const openSession = (session) => { setActiveSession(session); appendLog({ type: 'video', videoId: session.videoId, title: sessionTitle(session) }); };
    const closeSession = useCallback(() => setActiveSession(null), []);
    useEffect(() => {
        if (!activeSession) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closeSession(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [activeSession, closeSession]);

    const needsDownRegulation = Boolean(scores?.hasData && (scores.quadrant?.id === 'running_hot' || scores.wellbeingTier === 'At Risk' || scores.safetyFlag));
    const showElevatedSupport = Boolean(scores?.hasData && scores.safetyFlag);
    const suggested = suggestForEnergy(energy).map(getResetTool).filter(Boolean);

    const ToolCard = ({ tool, highlight = false }) => (
        <button onClick={() => setActiveTool(tool)} className={`text-left rounded-3xl border shadow-sm p-5 transition-all group hover:shadow-md ${highlight ? 'bg-brand-light border-brand-primary/40' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
            <div className="flex items-center justify-between">
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-white text-slate-500 border-slate-200">{tool.intent}</span>
                <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1"><Timer size={12} /> {tool.minutes} min</span>
            </div>
            <h4 className="font-bold text-slate-800 mt-3 group-hover:text-brand-primary transition-colors">{tool.title}</h4>
            <p className="text-sm text-slate-500 mt-1.5">{tool.blurb}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary mt-4"><Play size={14} /> Begin</span>
        </button>
    );

    return (
        <div className="max-w-5xl mx-auto space-y-8 animate-fade-in pb-12">
            {activeTool && (
                <ResetTool tool={activeTool} energyBefore={energy} onFinish={finishTool} onClose={() => setActiveTool(null)} />
            )}

            {/* 1. Calm hero — no numbers, just tone */}
            <div className="rounded-3xl bg-gradient-to-br from-brand-primary to-brand-dark p-8 md:p-10 text-white shadow-sm relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl pointer-events-none" />
                <div className="relative">
                    <p className="text-xs font-bold uppercase tracking-widest text-white/70 mb-3">Reset &amp; Recharge</p>
                    {needsDownRegulation ? (
                        <>
                            <h1 className="text-2xl md:text-3xl font-black tracking-tight">You've been running hot. Let's bring the system down.</h1>
                            <p className="text-white/80 mt-3 max-w-xl">Nothing to fix, nothing to prove right now. Pick something short below and {copyFor('rechargeInvite', isCorporate)}</p>
                        </>
                    ) : (
                        <>
                            <h1 className="text-2xl md:text-3xl font-black tracking-tight">A few minutes to come back to level.</h1>
                            <p className="text-white/80 mt-3 max-w-xl">Short, guided resets you can run anywhere — breath, grounding, deep rest. Everything here is designed to slow things down.</p>
                        </>
                    )}
                    {(lastLabel || weekCount > 0) && (
                        <p className="text-xs text-white/60 mt-4 inline-flex items-center gap-1.5"><History size={13} /> {lastLabel ? `Last reset ${lastLabel}` : ''}{weekCount > 0 ? `${lastLabel ? ' · ' : ''}${weekCount} this week` : ''}</p>
                    )}
                </div>
            </div>

            {showElevatedSupport && <SupportCard elevated onNavigate={onNavigate} currentUser={currentUser} />}

            {/* 2. Energy check-in -> suggestion */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-slate-800">How much do you have right now?</h2>
                <p className="text-sm text-slate-500 mt-1">One honest number. It picks the right length of reset — and only you can see it.</p>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 mt-4">
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                        <button key={n} onClick={() => setEnergy(n)} className={`h-11 sm:h-10 rounded-xl text-sm font-black border transition-colors ${energy === n ? 'bg-brand-primary border-brand-primary text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-brand-primary/50'}`}>{n}</button>
                    ))}
                </div>
                <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1.5"><span>Empty</span><span>Full</span></div>
                {energy != null && (
                    <div className="mt-5 animate-in fade-in slide-in-from-top-1">
                        <p className="text-sm font-bold text-slate-700 mb-3">{suggestionCopy(energy)}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {suggested.map(t => <ToolCard key={t.id} tool={t} highlight />)}
                        </div>
                    </div>
                )}
            </div>

            {/* 3. Tools we own */}
            <div>
                <h2 className="text-xl font-bold text-slate-800">Guided resets</h2>
                <p className="text-sm text-slate-500 mt-1">Timed and cued on screen. Two to ten minutes. Finishing one counts on your Guided Path.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    {RESET_TOOLS.map(t => <ToolCard key={t.id} tool={t} />)}
                </div>
            </div>

            {/* 4. Evening: wind-down routine + decompress */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <WindDownCard uid={uid} onStart={(tool) => setActiveTool(tool)} lastRun={lastWindDown} />
                <DecompressCard uid={uid} currentUser={currentUser} />
            </div>

            {/* 5. Third-party shelf — honest label */}
            <div className="space-y-5">
                <div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Youtube size={20} className="text-slate-400" /> Sessions from around the web</h2>
                    <p className="text-sm text-slate-500 mt-1">Curated videos on YouTube by other creators — not produced by EAS. They open in a player here; the creator is shown on each. Your organization can add its own in the Content Manager.</p>
                </div>
                {shelf.map(cat => {
                    const CatIcon = cat.icon;
                    return (
                        <div key={cat.id}>
                            <div className="flex items-center gap-2 mb-2"><CatIcon size={16} className="text-slate-400" /><h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">{cat.label}</h3></div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {cat.sessions.map(session => (
                                    <button key={session.id} onClick={() => openSession(session)} className="text-left bg-slate-50 rounded-2xl border border-slate-200 p-4 hover:bg-white hover:border-slate-300 transition-all group">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cat.tagClass}`}>{cat.label}</span>
                                            <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1 truncate"><Youtube size={11} /> {session.source}</span>
                                        </div>
                                        <h4 className="font-bold text-slate-800 mt-2 text-sm group-hover:text-brand-primary transition-colors">{sessionTitle(session)}</h4>
                                        <p className="text-xs text-slate-500 mt-1">{session.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {!showElevatedSupport && <SupportCard elevated={false} onNavigate={onNavigate} currentUser={currentUser} />}

            {/* Web session player */}
            {activeSession && (
                <div className="fixed inset-0 z-[200] bg-slate-900/90 flex items-center justify-center p-4 md:p-10 animate-fade-in">
                    <button onClick={closeSession} aria-label="Close session" className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"><X size={22} /></button>
                    <div className="w-full max-w-4xl">
                        <p className="text-white/70 text-sm font-semibold mb-3 text-center">{sessionTitle(activeSession)} <span className="text-white/40">· {activeSession.source} on YouTube</span></p>
                        <div className="aspect-video w-full"><CustomYouTubePlayer videoId={activeSession.videoId} /></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RechargeHub;
